/**
 * Comercial Planos & Pricing — Governance (Sistema A em BRL)
 *
 * Lista, cria, edita e arquiva planos do Sistema A:
 *   plan_definitions + quota_policies (BRL, family×band, fonte oficial)
 *
 * Endpoints:
 *   GET    /api/governance/comercial/plans?active_only=true|false&family=...
 *   POST   /api/governance/comercial/plans
 *   PATCH  /api/governance/comercial/plans/{plan_key}
 *   POST   /api/governance/comercial/plans/{plan_key}/archive
 *
 * Decisões:
 *   - quotas obrigatórias: ai_credits, athletes, staff_seats, storage_mb
 *   - api_calls opcional (só enterprise tem)
 *   - reativar via PATCH is_active=true (sem endpoint próprio)
 *   - archive bloqueado (409) se houver subscription ativa
 *   - plan_key, plan_family, size_band são imutáveis no PATCH
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, ChevronRight, RefreshCw, AlertCircle,
  Plus, Archive, ArchiveRestore, Edit3, X,
} from 'lucide-react';
import { AZURE } from '../../services/api';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD  = '#b48c2c';

// ─────────────────────────────────────────────────────────────────────────────
// i18n
// ─────────────────────────────────────────────────────────────────────────────

const COMERCIAL_LABELS = {
  pt: {
    pageTitle: 'Planos & Pricing',
    pageSubtitle: 'Sistema A — plan_definitions + quota_policies',
    loadingDesc: 'Carregando dados do banco…',
    plansDesc: (n) => `${n} ${n === 1 ? 'plano' : 'planos'} — dados reais (Sistema A em BRL)`,
    viewClients: 'Ver Clientes',
    newPlan: 'Novo plano',
    showArchived: 'Mostrar arquivados',
    metrics: ['Planos ativos', 'Entrada', 'Topo', 'Famílias'],
    metricNotes: ['de {n} total', 'menor preço ativo', 'maior preço', 'tipos de plano'],
    all: (n) => `Todos (${n})`,
    edit: 'Editar',
    archive: 'Arquivar',
    reactivate: 'Reativar',
    inactive: 'Arquivado',
    activeSubs: (n) => `${n} ${n === 1 ? 'assinatura' : 'assinaturas'} ativa${n === 1 ? '' : 's'}`,
    emptyList: 'Nenhum plano para exibir.',
    detail: {
      quotas: 'Limites',
      family: 'Família',
      sizeBand: 'Tamanho',
      price: 'Preço mensal',
      warnAt: 'Alerta em',
      hardLimit: 'Limite rígido',
      resource: 'Recurso',
      monthlyLimit: 'Limite mensal',
      yes: 'Sim',
      no: 'Não',
    },
    cta: 'Precisa de uma proposta customizada?',
    ctaBtn: 'Ir para Clientes',
    modal: {
      titleCreate: 'Novo plano',
      titleEdit: 'Editar plano',
      fields: {
        plan_key: 'Identificador (ex: pro_l)',
        plan_name: 'Nome',
        plan_family: 'Família',
        size_band: 'Tamanho',
        price_brl: 'Preço mensal (em centavos)',
        tier_order: 'Ordem (tier_order)',
        description: 'Descrição',
        is_public: 'Visível publicamente',
        quotas: 'Limites mensais',
      },
      placeholders: {
        plan_key: 'pro_xl',
        plan_name: 'VUKZ Pro XL',
      },
      help: {
        plan_key_immutable: 'Imutável depois de criado',
        family_immutable: 'Imutável depois de criado',
        enterprise_no_band: 'Enterprise não tem tamanho (S/M/L)',
        enterprise_no_price: 'Enterprise pode ter preço sob consulta (deixe vazio)',
        price_in_cents: 'em centavos · {brl}',
      },
      quotaLabels: {
        ai_credits:  'Créditos AI (interações/mês)',
        athletes:    'Atletas',
        staff_seats: 'Vagas de staff',
        storage_mb:  'Armazenamento (MB)',
        api_calls:   'Chamadas API (opcional)',
      },
      saving: 'Salvando…',
      save: 'Salvar',
      create: 'Criar plano',
      cancel: 'Cancelar',
      errorDefault: 'Erro ao salvar',
      yesLabel: 'Sim',
      noSalesLed: 'Não (sales-led)',
      hardLabel: 'Hard',
      warnLabel: 'Alerta %',
    },
    archiveModal: {
      title: 'Arquivar plano',
      titleReactivate: 'Reativar plano',
      confirm: (name) => `Confirmar arquivar "${name}"?\nEle deixará de ser ofertável, mas o histórico fica preservado.`,
      reactivateConfirm: (name) => `Reativar "${name}"?`,
      blocked: 'Plano em uso',
      blockedDesc: (n) => `Este plano tem ${n} ${n === 1 ? 'assinatura ativa' : 'assinaturas ativas'}. Migre as assinaturas antes de arquivar.`,
      proceed: 'Arquivar',
      cancel: 'Cancelar',
      reactivate: 'Reativar',
    },
    families: { basic: 'Basic', pro: 'Pro', enterprise: 'Enterprise' },
    bands: { S: 'S', M: 'M', L: 'L' },
    tiles: { athletes: 'Atletas', staff: 'Staff', ai: 'Créditos AI', storage: 'Storage MB' },
    consultLabel: 'Sob consulta',
  },
  en: {
    pageTitle: 'Plans & Pricing',
    pageSubtitle: 'System A — plan_definitions + quota_policies',
    loadingDesc: 'Loading database data…',
    plansDesc: (n) => `${n} plan${n === 1 ? '' : 's'} — live data (System A in BRL)`,
    viewClients: 'View Clients',
    newPlan: 'New plan',
    showArchived: 'Show archived',
    metrics: ['Active plans', 'Entry', 'Top', 'Families'],
    metricNotes: ['of {n} total', 'lowest active price', 'highest price', 'plan types'],
    all: (n) => `All (${n})`,
    edit: 'Edit',
    archive: 'Archive',
    reactivate: 'Reactivate',
    inactive: 'Archived',
    activeSubs: (n) => `${n} active subscription${n === 1 ? '' : 's'}`,
    emptyList: 'No plans to display.',
    detail: {
      quotas: 'Limits',
      family: 'Family',
      sizeBand: 'Size',
      price: 'Monthly price',
      warnAt: 'Warn at',
      hardLimit: 'Hard limit',
      resource: 'Resource',
      monthlyLimit: 'Monthly limit',
      yes: 'Yes',
      no: 'No',
    },
    cta: 'Need a custom proposal?',
    ctaBtn: 'Go to Clients',
    modal: {
      titleCreate: 'New plan',
      titleEdit: 'Edit plan',
      fields: {
        plan_key: 'Identifier (e.g. pro_l)',
        plan_name: 'Name',
        plan_family: 'Family',
        size_band: 'Size',
        price_brl: 'Monthly price (in cents)',
        tier_order: 'Order (tier_order)',
        description: 'Description',
        is_public: 'Publicly visible',
        quotas: 'Monthly limits',
      },
      placeholders: { plan_key: 'pro_xl', plan_name: 'VUKZ Pro XL' },
      help: {
        plan_key_immutable: 'Immutable after creation',
        family_immutable: 'Immutable after creation',
        enterprise_no_band: 'Enterprise has no size (S/M/L)',
        enterprise_no_price: 'Enterprise can have on-request pricing (leave empty)',
        price_in_cents: 'in cents · {brl}',
      },
      quotaLabels: {
        ai_credits:  'AI credits (interactions/mo)',
        athletes:    'Athletes',
        staff_seats: 'Staff seats',
        storage_mb:  'Storage (MB)',
        api_calls:   'API calls (optional)',
      },
      saving: 'Saving…',
      save: 'Save',
      create: 'Create plan',
      cancel: 'Cancel',
      errorDefault: 'Error saving',
      yesLabel: 'Yes',
      noSalesLed: 'No (sales-led)',
      hardLabel: 'Hard',
      warnLabel: 'Warn %',
    },
    archiveModal: {
      title: 'Archive plan',
      titleReactivate: 'Reactivate plan',
      confirm: (name) => `Confirm archiving "${name}"?\nIt will no longer be sellable, but history is preserved.`,
      reactivateConfirm: (name) => `Reactivate "${name}"?`,
      blocked: 'Plan in use',
      blockedDesc: (n) => `This plan has ${n} active subscription${n === 1 ? '' : 's'}. Migrate them before archiving.`,
      proceed: 'Archive',
      cancel: 'Cancel',
      reactivate: 'Reactivate',
    },
    families: { basic: 'Basic', pro: 'Pro', enterprise: 'Enterprise' },
    bands: { S: 'S', M: 'M', L: 'L' },
    tiles: { athletes: 'Athletes', staff: 'Staff', ai: 'AI credits', storage: 'Storage MB' },
    consultLabel: 'On request',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Theme by family
// ─────────────────────────────────────────────────────────────────────────────

const FAMILY_CONFIG = {
  basic:      { color: '#6b7280', popular: false },
  pro:        { color: BRAND_GOLD, popular: true  },
  enterprise: { color: '#111111', popular: false },
};

const REQUIRED_QUOTAS = ['ai_credits', 'athletes', 'staff_seats', 'storage_mb'];
const ALL_QUOTAS      = [...REQUIRED_QUOTAS, 'api_calls'];

const fmtBRL = (cents, consultLabel = 'Sob consulta') => {
  if (cents == null) return consultLabel;
  const v = Number(cents) / 100;
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 });
};

const fmtInt = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR'));

const getQuota = (plan, resource_type) => {
  const q = (plan.quotas || []).find((x) => x.resource_type === resource_type);
  return q ? q.monthly_limit : null;
};

// ─────────────────────────────────────────────────────────────────────────────
// PlanCard
// ─────────────────────────────────────────────────────────────────────────────

const PlanCard = ({ plan, selected, onSelect, onEdit, onArchive, t }) => {
  const fam        = FAMILY_CONFIG[plan.plan_family] || { color: '#6b7280', popular: false };
  const isSelected = selected === plan.plan_key;
  const aiCredits  = getQuota(plan, 'ai_credits');
  const athletes   = getQuota(plan, 'athletes');
  const staffSeats = getQuota(plan, 'staff_seats');
  const storageMb  = getQuota(plan, 'storage_mb');

  return (
    <div
      className={`bg-white rounded-xl border-2 p-5 transition-all cursor-pointer hover:shadow-md relative ${
        isSelected ? 'shadow-md' : 'border-gray-200'
      }`}
      style={isSelected ? { borderColor: fam.color } : {}}
      onClick={() => onSelect(isSelected ? null : plan.plan_key)}
    >
      {fam.popular && plan.is_active && (
        <span
          className="inline-block px-2 py-0.5 text-[10px] font-bold rounded mb-2"
          style={{ backgroundColor: BRAND_GOLD, color: '#fff' }}
        >
          POPULAR
        </span>
      )}
      {!plan.is_active && (
        <span className="absolute top-3 right-3 px-2 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-600">
          {t.inactive}
        </span>
      )}

      <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: fam.color }}>
        {t.families[plan.plan_family] || plan.plan_family}
        {plan.size_band && <span className="ml-1 text-gray-400">· {plan.size_band}</span>}
      </div>
      <h3 className="text-lg font-bold" style={{ color: fam.color }}>
        {plan.plan_name}
      </h3>
      <div className="text-xl font-extrabold text-gray-900 mt-1">
        {fmtBRL(plan.price_brl_cents, t.consultLabel)}
      </div>
      <div className="text-xs text-gray-500 mt-0.5 font-mono">{plan.plan_key}</div>

      {/* 4 quotas em grid */}
      <div className="grid grid-cols-2 gap-2 mt-4">
        <QuotaTile label={t.tiles.athletes} value={fmtInt(athletes)}   color={fam.color} />
        <QuotaTile label={t.tiles.staff}    value={fmtInt(staffSeats)} color={fam.color} />
        <QuotaTile label={t.tiles.ai}       value={fmtInt(aiCredits)}  color={fam.color} />
        <QuotaTile label={t.tiles.storage}  value={fmtInt(storageMb)}  color={fam.color} />
      </div>

      {/* badge subscriptions ativas */}
      {plan.active_subscriptions_count > 0 && (
        <div className="mt-3 text-[11px] text-gray-600 bg-gray-50 px-2 py-1 rounded">
          <span className="font-semibold" style={{ color: BRAND_GREEN }}>•</span>{' '}
          {t.activeSubs(plan.active_subscriptions_count)}
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex gap-2">
        <button
          onClick={(ev) => { ev.stopPropagation(); onEdit(plan); }}
          className="flex-1 text-xs py-1.5 rounded-lg border border-gray-200 hover:border-gray-400 text-gray-700 hover:text-gray-900 transition-colors flex items-center justify-center gap-1"
        >
          <Edit3 className="w-3 h-3" /> {t.edit}
        </button>
        <button
          onClick={(ev) => { ev.stopPropagation(); onArchive(plan); }}
          className="text-xs py-1.5 px-3 rounded-lg border border-gray-200 hover:border-gray-400 text-gray-700 hover:text-gray-900 transition-colors flex items-center gap-1"
          title={plan.is_active ? t.archive : t.reactivate}
        >
          {plan.is_active ? <Archive className="w-3 h-3" /> : <ArchiveRestore className="w-3 h-3" />}
        </button>
      </div>
    </div>
  );
};

const QuotaTile = ({ label, value, color }) => (
  <div className="bg-gray-50 rounded-lg px-2 py-1.5 text-center">
    <div className="text-base font-bold" style={{ color }}>{value}</div>
    <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Plan Modal (criar + editar)
// ─────────────────────────────────────────────────────────────────────────────

const PlanModal = ({ plan, onClose, onSaved, t }) => {
  const m        = t.modal;
  const isCreate = !plan;

  const initial = isCreate
    ? {
        plan_key: '',
        plan_name: '',
        plan_family: 'pro',
        size_band: 'M',
        price_brl_cents: 0,
        tier_order: 50,
        description: '',
        is_public: true,
        quotas: {
          ai_credits:  { monthly_limit: 1000, hard_limit: true, warn_at_pct: 80 },
          athletes:    { monthly_limit: 10,   hard_limit: true, warn_at_pct: 80 },
          staff_seats: { monthly_limit: 3,    hard_limit: true, warn_at_pct: 80 },
          storage_mb:  { monthly_limit: 1024, hard_limit: true, warn_at_pct: 80 },
        },
      }
    : {
        plan_key: plan.plan_key,
        plan_name: plan.plan_name,
        plan_family: plan.plan_family,
        size_band: plan.size_band,
        price_brl_cents: plan.price_brl_cents == null ? null : plan.price_brl_cents,
        tier_order: plan.tier_order,
        description: plan.description || '',
        is_public: !!plan.is_public,
        quotas: Object.fromEntries(
          (plan.quotas || []).map((q) => [
            q.resource_type,
            {
              monthly_limit: q.monthly_limit,
              hard_limit: q.hard_limit,
              warn_at_pct: q.warn_at_pct,
            },
          ])
        ),
      };

  const [form, setForm]     = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const setQuota = (rt, k, v) =>
    setForm((prev) => ({
      ...prev,
      quotas: {
        ...prev.quotas,
        [rt]: { ...(prev.quotas[rt] || { hard_limit: true, warn_at_pct: 80 }), [k]: v },
      },
    }));

  const isEnterprise = form.plan_family === 'enterprise';

  // Quando muda família para enterprise, zera size_band
  useEffect(() => {
    if (isEnterprise && form.size_band) setField('size_band', null);
    if (!isEnterprise && !form.size_band) setField('size_band', 'M');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnterprise]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isCreate) {
        const body = {
          plan_key: form.plan_key.trim(),
          plan_name: form.plan_name.trim(),
          plan_family: form.plan_family,
          size_band: isEnterprise ? null : form.size_band,
          price_brl_cents:
            form.price_brl_cents === '' || form.price_brl_cents == null
              ? (isEnterprise ? null : 0)
              : parseInt(form.price_brl_cents, 10),
          tier_order: parseInt(form.tier_order, 10) || 0,
          description: form.description || null,
          is_public: !!form.is_public,
          quotas: {},
        };
        REQUIRED_QUOTAS.forEach((rt) => {
          const q = form.quotas[rt];
          if (q && q.monthly_limit > 0) {
            body.quotas[rt] = {
              monthly_limit: parseInt(q.monthly_limit, 10),
              hard_limit: !!q.hard_limit,
              warn_at_pct: parseInt(q.warn_at_pct, 10) || 80,
            };
          }
        });
        if (form.quotas.api_calls && form.quotas.api_calls.monthly_limit > 0) {
          body.quotas.api_calls = {
            monthly_limit: parseInt(form.quotas.api_calls.monthly_limit, 10),
            hard_limit: !!form.quotas.api_calls.hard_limit,
            warn_at_pct: parseInt(form.quotas.api_calls.warn_at_pct, 10) || 80,
          };
        }
        await AZURE.post('/governance/comercial/plans', body);
      } else {
        // PATCH — plan_key/family/size_band imutáveis (não enviar)
        const body = {
          plan_name: form.plan_name.trim(),
          price_brl_cents:
            form.price_brl_cents === '' || form.price_brl_cents == null
              ? null
              : parseInt(form.price_brl_cents, 10),
          tier_order: parseInt(form.tier_order, 10) || 0,
          description: form.description || null,
          is_public: !!form.is_public,
          quotas: {},
        };
        ALL_QUOTAS.forEach((rt) => {
          const q = form.quotas[rt];
          if (q && q.monthly_limit > 0) {
            body.quotas[rt] = {
              monthly_limit: parseInt(q.monthly_limit, 10),
              hard_limit: !!q.hard_limit,
              warn_at_pct: parseInt(q.warn_at_pct, 10) || 80,
            };
          }
        });
        await AZURE.patch(`/governance/comercial/plans/${form.plan_key}`, body);
      }
      onSaved();
    } catch (e) {
      const msg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.error ||
        e?.message ||
        m.errorDefault;
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">{isCreate ? m.titleCreate : m.titleEdit}</h3>
            {!isCreate && <p className="text-xs text-gray-500 mt-0.5 font-mono">{form.plan_key}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Identidade */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Field label={m.fields.plan_key} help={!isCreate ? m.help.plan_key_immutable : null}>
            <input
              type="text"
              value={form.plan_key}
              onChange={(e) => setField('plan_key', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              placeholder={m.placeholders.plan_key}
              disabled={!isCreate}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-50 disabled:text-gray-500"
            />
          </Field>
          <Field label={m.fields.plan_name}>
            <input
              type="text"
              value={form.plan_name}
              onChange={(e) => setField('plan_name', e.target.value)}
              placeholder={m.placeholders.plan_name}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-3">
          <Field label={m.fields.plan_family} help={!isCreate ? m.help.family_immutable : null}>
            <select
              value={form.plan_family}
              onChange={(e) => setField('plan_family', e.target.value)}
              disabled={!isCreate}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            >
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </Field>
          <Field label={m.fields.size_band} help={isEnterprise ? m.help.enterprise_no_band : null}>
            <select
              value={form.size_band || ''}
              onChange={(e) => setField('size_band', e.target.value || null)}
              disabled={!isCreate || isEnterprise}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            >
              {isEnterprise
                ? <option value="">—</option>
                : ['S', 'M', 'L'].map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          <Field label={m.fields.tier_order}>
            <input
              type="number"
              value={form.tier_order}
              onChange={(e) => setField('tier_order', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <Field label={m.fields.price_brl} help={isEnterprise ? m.help.enterprise_no_price : null}>
            <input
              type="number"
              min="0"
              step="100"
              value={form.price_brl_cents == null ? '' : form.price_brl_cents}
              onChange={(e) =>
                setField(
                  'price_brl_cents',
                  e.target.value === '' ? (isEnterprise ? null : 0) : parseInt(e.target.value, 10)
                )
              }
              placeholder={isEnterprise ? 'null = sob consulta' : '0'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <p className="text-[10px] text-gray-500 mt-0.5">
              {m.help.price_in_cents.replace('{brl}', fmtBRL(form.price_brl_cents, t.consultLabel))}
            </p>
          </Field>
          <Field label={m.fields.is_public}>
            <label className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={!!form.is_public}
                onChange={(e) => setField('is_public', e.target.checked)}
              />
              <span className="text-sm text-gray-700">{form.is_public ? m.yesLabel : m.noSalesLed}</span>
            </label>
          </Field>
        </div>

        <Field label={m.fields.description}>
          <textarea
            rows={2}
            value={form.description || ''}
            onChange={(e) => setField('description', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </Field>

        {/* Quotas */}
        <div className="mt-4 mb-2">
          <h4 className="text-sm font-bold text-gray-800">{m.fields.quotas}</h4>
        </div>
        <div className="space-y-2">
          {REQUIRED_QUOTAS.map((rt) => {
            const q = form.quotas[rt] || { monthly_limit: 0, hard_limit: true, warn_at_pct: 80 };
            return (
              <QuotaRow
                key={rt}
                resourceType={rt}
                quota={q}
                setQuota={setQuota}
                required
                label={m.quotaLabels[rt]}
                m={m}
              />
            );
          })}
          {/* api_calls opcional */}
          <QuotaRow
            resourceType="api_calls"
            quota={form.quotas.api_calls || { monthly_limit: 0, hard_limit: true, warn_at_pct: 80 }}
            setQuota={setQuota}
            label={m.quotaLabels.api_calls}
            m={m}
          />
        </div>

        {error && (
          <p className="text-xs text-red-600 mt-3 bg-red-50 border border-red-200 rounded-lg p-2">{error}</p>
        )}

        <div className="flex gap-2 mt-5">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-60"
            style={{ backgroundColor: BRAND_GREEN }}
          >
            {saving ? m.saving : isCreate ? m.create : m.save}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600"
          >
            {m.cancel}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, help, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
    {children}
    {help && <p className="text-[10px] text-gray-400 mt-0.5">{help}</p>}
  </div>
);

const QuotaRow = ({ resourceType, quota, setQuota, required, label, m }) => (
  <div className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded-lg p-2">
    <div className="col-span-4 text-xs">
      <div className="font-semibold text-gray-800">{label}</div>
      <div className="font-mono text-[10px] text-gray-500">
        {resourceType}{required ? '' : ' (opcional)'}
      </div>
    </div>
    <div className="col-span-3">
      <input
        type="number"
        min="0"
        value={quota.monthly_limit || 0}
        onChange={(e) => setQuota(resourceType, 'monthly_limit', parseInt(e.target.value, 10) || 0)}
        placeholder="monthly_limit"
        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
      />
    </div>
    <div className="col-span-3">
      <input
        type="number"
        min="1"
        max="100"
        value={quota.warn_at_pct || 80}
        onChange={(e) => setQuota(resourceType, 'warn_at_pct', parseInt(e.target.value, 10) || 80)}
        title={m.warnLabel}
        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
      />
    </div>
    <div className="col-span-2 text-center">
      <label className="flex items-center gap-1 justify-center text-[11px] text-gray-700">
        <input
          type="checkbox"
          checked={!!quota.hard_limit}
          onChange={(e) => setQuota(resourceType, 'hard_limit', e.target.checked)}
        />
        {m.hardLabel}
      </label>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Archive Modal
// ─────────────────────────────────────────────────────────────────────────────

const ArchiveModal = ({ plan, onClose, onDone, t }) => {
  const m = t.archiveModal;
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);
  const blocked           = plan.is_active && plan.active_subscriptions_count > 0;

  const handle = async () => {
    setBusy(true);
    setError(null);
    try {
      if (plan.is_active) {
        await AZURE.post(`/governance/comercial/plans/${plan.plan_key}/archive`);
      } else {
        await AZURE.patch(`/governance/comercial/plans/${plan.plan_key}`, { is_active: true });
      }
      onDone();
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.response?.data?.error || e?.message || 'Erro';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-gray-900 text-lg mb-2">
          {plan.is_active ? m.title : m.titleReactivate}
        </h3>
        <p className="text-sm text-gray-700 mb-3">
          <span className="font-semibold">{plan.plan_name}</span> ·{' '}
          <span className="font-mono text-xs text-gray-500">{plan.plan_key}</span>
        </p>

        {blocked ? (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="font-semibold text-amber-900 mb-1 text-sm">{m.blocked}</div>
              <p className="text-xs text-amber-800">{m.blockedDesc(plan.active_subscriptions_count)}</p>
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600">
                {m.cancel}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 mb-4 whitespace-pre-line">
              {plan.is_active ? m.confirm(plan.plan_name) : m.reactivateConfirm(plan.plan_name)}
            </p>
            {error && (
              <p className="text-xs text-red-600 mb-3 bg-red-50 border border-red-200 rounded-lg p-2">{error}</p>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600">
                {m.cancel}
              </button>
              <button
                onClick={handle}
                disabled={busy}
                className="px-4 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-60"
                style={{ backgroundColor: plan.is_active ? '#b91c1c' : BRAND_GREEN }}
              >
                {busy ? '…' : plan.is_active ? m.proceed : m.reactivate}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

const GovernanceComercialPage = () => {
  const { lang }   = useGovernanceLang();
  const t          = COMERCIAL_LABELS[lang] || COMERCIAL_LABELS.pt;
  const [plans,          setPlans]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [selectedKey,    setSelectedKey]    = useState(null);
  const [editingPlan,    setEditingPlan]    = useState(null);
  const [creating,       setCreating]       = useState(false);
  const [archivingPlan,  setArchivingPlan]  = useState(null);
  const [filterFamily,   setFilterFamily]   = useState('');
  const [showInactive,   setShowInactive]   = useState(false);

  const loadPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const url       = `/governance/comercial/plans?active_only=${showInactive ? 'false' : 'true'}`;
      const { data }  = await AZURE.get(url);
      // envelope: { success, schema_version, data: { plans, total } } ou { plans, total } legacy
      const payload   = data?.data || data;
      setPlans(payload.plans || []);
    } catch (e) {
      const msg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Erro ao carregar planos';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const families     = useMemo(
    () => Array.from(new Set(plans.map((p) => p.plan_family))).filter(Boolean),
    [plans]
  );
  const filtered     = useMemo(
    () => (filterFamily ? plans.filter((p) => p.plan_family === filterFamily) : plans),
    [plans, filterFamily]
  );
  const activePlans  = plans.filter((p) => p.is_active).length;
  const pricedActive = plans.filter((p) => p.is_active && p.price_brl_cents > 0);
  const minPrice     = pricedActive.length ? Math.min(...pricedActive.map((p) => p.price_brl_cents)) : 0;
  const maxPrice     = pricedActive.length ? Math.max(...pricedActive.map((p) => p.price_brl_cents)) : 0;

  const METRICS = [
    {
      label: t.metrics[0],
      value: loading ? '…' : String(activePlans),
      note:  t.metricNotes[0].replace('{n}', plans.length),
    },
    {
      label: t.metrics[1],
      value: loading ? '…' : fmtBRL(minPrice, t.consultLabel),
      note:  t.metricNotes[1],
    },
    {
      label: t.metrics[2],
      value: loading ? '…' : fmtBRL(maxPrice, t.consultLabel),
      note:  t.metricNotes[2],
    },
    {
      label: t.metrics[3],
      value: loading ? '…' : String(families.length),
      note:  t.metricNotes[3],
    },
  ];

  const selectedPlanObj = plans.find((p) => p.plan_key === selectedKey);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <img
            src="/vukz-logo.png"
            alt="Vukz"
            className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div>
            <h1 className="text-gray-900 text-2xl font-extrabold">{t.pageTitle}</h1>
            <p className="text-gray-500 mt-1 text-sm">
              {loading ? t.loadingDesc : t.plansDesc(plans.length)}
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">{t.pageSubtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            {t.showArchived}
          </label>
          <button
            onClick={loadPlans}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-bold text-white"
            style={{ backgroundColor: BRAND_GREEN }}
          >
            <Plus className="w-4 h-4" /> {t.newPlan}
          </button>
          <Link
            to="/governance/comercial/clientes"
            className="flex items-center gap-1 px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            <Users className="w-4 h-4" /> {t.viewClients} <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {METRICS.map((mm) => (
          <div key={mm.label} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-xl font-bold" style={{ color: BRAND_GREEN }}>{mm.value}</div>
            <div className="text-xs font-medium text-gray-900 mt-1">{mm.label}</div>
            <div className="text-xs text-gray-500">{mm.note}</div>
          </div>
        ))}
      </div>

      {/* Filtros por family */}
      {!loading && families.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilterFamily('')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              !filterFamily
                ? 'text-white border-transparent'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
            }`}
            style={!filterFamily ? { backgroundColor: BRAND_GREEN } : {}}
          >
            {t.all(plans.length)}
          </button>
          {families.map((f) => (
            <button
              key={f}
              onClick={() => setFilterFamily(filterFamily === f ? '' : f)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                filterFamily === f
                  ? 'text-white border-transparent'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
              style={filterFamily === f ? { backgroundColor: FAMILY_CONFIG[f]?.color || BRAND_GREEN } : {}}
            >
              {t.families[f] || f} ({plans.filter((p) => p.plan_family === f).length})
            </button>
          ))}
        </div>
      )}

      {/* Plans Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 h-64 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500 text-sm">
          {t.emptyList}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {filtered.map((plan) => (
            <PlanCard
              key={plan.plan_key}
              plan={plan}
              selected={selectedKey}
              onSelect={setSelectedKey}
              onEdit={setEditingPlan}
              onArchive={setArchivingPlan}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Detail panel — quotas detalhadas */}
      {selectedPlanObj && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div
                className="text-xs font-bold uppercase tracking-wider mb-1"
                style={{ color: FAMILY_CONFIG[selectedPlanObj.plan_family]?.color || '#6b7280' }}
              >
                {t.families[selectedPlanObj.plan_family] || selectedPlanObj.plan_family}
                {selectedPlanObj.size_band && <span className="ml-1 text-gray-400">· {selectedPlanObj.size_band}</span>}
              </div>
              <h2 className="text-xl font-extrabold text-gray-900">{selectedPlanObj.plan_name}</h2>
              <p className="text-sm text-gray-500">
                <span className="font-mono">{selectedPlanObj.plan_key}</span> ·{' '}
                {fmtBRL(selectedPlanObj.price_brl_cents, t.consultLabel)}/mês
              </p>
              {selectedPlanObj.active_subscriptions_count > 0 && (
                <p className="text-xs text-gray-600 mt-1">
                  {t.activeSubs(selectedPlanObj.active_subscriptions_count)}
                </p>
              )}
            </div>
            <button onClick={() => setSelectedKey(null)} className="text-gray-400 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          {selectedPlanObj.description && (
            <p className="text-sm text-gray-600 mb-4">{selectedPlanObj.description}</p>
          )}

          <h3 className="text-sm font-bold text-gray-700 mb-3">{t.detail.quotas}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs text-gray-500 py-1 pr-4">{t.detail.resource}</th>
                  <th className="text-right text-xs text-gray-500 py-1 pr-4">{t.detail.monthlyLimit}</th>
                  <th className="text-right text-xs text-gray-500 py-1 pr-4">{t.detail.warnAt}</th>
                  <th className="text-center text-xs text-gray-500 py-1">{t.detail.hardLimit}</th>
                </tr>
              </thead>
              <tbody>
                {(selectedPlanObj.quotas || []).map((q) => (
                  <tr key={q.resource_type} className="border-b border-gray-50">
                    <td className="py-1.5 pr-4">
                      <span className="font-medium text-gray-800">
                        {t.modal.quotaLabels[q.resource_type] || q.resource_type}
                      </span>
                      <span className="ml-2 font-mono text-[10px] text-gray-400">{q.resource_type}</span>
                    </td>
                    <td className="text-right pr-4 text-gray-700 font-semibold">{fmtInt(q.monthly_limit)}</td>
                    <td className="text-right pr-4 text-gray-600">{q.warn_at_pct}%</td>
                    <td className="text-center text-gray-600">{q.hard_limit ? t.detail.yes : t.detail.no}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 text-center">
        <p className="text-gray-600 mb-3 text-sm">{t.cta}</p>
        <Link
          to="/governance/comercial/clientes"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-bold text-sm"
          style={{ backgroundColor: BRAND_GREEN }}
        >
          <Users className="w-4 h-4" /> {t.ctaBtn}
        </Link>
      </div>

      {(editingPlan || creating) && (
        <PlanModal
          plan={editingPlan}
          onClose={() => { setEditingPlan(null); setCreating(false); }}
          onSaved={() => { setEditingPlan(null); setCreating(false); loadPlans(); }}
          t={t}
        />
      )}

      {archivingPlan && (
        <ArchiveModal
          plan={archivingPlan}
          onClose={() => setArchivingPlan(null)}
          onDone={() => { setArchivingPlan(null); loadPlans(); }}
          t={t}
        />
      )}
    </div>
  );
};

export default GovernanceComercialPage;
